import cloneDeep from "lodash/cloneDeep";
import type { FC, PropsWithChildren } from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import type {
	CurveSettings,
	Research,
	RootDispatch,
	RootState,
	TabletSettings,
} from "@/components/WellLoggingOperations/types";
import type {
	TabletSettingsContext as ITabletSettingsContext,
	SelectedWell,
} from "@/components/WellLoggingOperations/types/tabletSettings";
import { defaultTabletSettings } from "@/store/well-logs/tabletSettings";
import { mapResearchesByKey } from "./Researches/helpers";

/**
 * Контекст для настроек планшета с значениями по умолчанию
 */
const TabletSettingsContext = createContext<ITabletSettingsContext>({
	selectedWells: null,
	tabletSettings: defaultTabletSettings,
	tabletsSettings: {},
	updateTabletSettings: () => null,
	setSelectedWells: () => null,
	onSave: () => null,
	showResearchSettings: () => null,
});

const prepareDataByCurrentTemplate = (
	data: TabletSettings,
	templateCurvesSettings: Record<string, CurveSettings>,
	researchMap: Record<string, Research>,
): TabletSettings => {
	const newCurveSettings: Record<string, CurveSettings> = {};
	for (const [key, curveSetting] of Object.entries(data.curveSettings)) {
		newCurveSettings[key] =
			templateCurvesSettings[curveSetting.title] ?? curveSetting;
	}
	for (const researchKey of data.wellLoggingKeys) {
		const templateCurveSetting =
			templateCurvesSettings[researchMap[researchKey]?.type];
		if (templateCurveSetting) {
			newCurveSettings[researchKey] = templateCurveSetting;
		}
	}
	return { ...data, curveSettings: newCurveSettings };
};

/**
 * Провайдер для настроек планшета
 * Управляет состоянием настроек и предоставляет методы для их обновления
 */
export interface TabletSettingsProviderProps {
	onClose: () => void;
	showResearchSettings: () => void;
}

export const TabletSettingsProvider: FC<
	PropsWithChildren<TabletSettingsProviderProps>
> = ({ onClose, showResearchSettings, children }) => {
	// Локальное состояние для хранения измененных настроек планшета
	// Ключ - идентификатор скважины, значение - настройки планшета
	const [state, setState] = useState<Record<string, TabletSettings>>({});
	const templateCurvesSettings = useSelector(
		(state: RootState) => state.templates.currentTemplateCurveSettings,
	);
	const researches = useSelector((state: RootState) => state.researches.data);

	// Выбранная скважина (скважина из настроек планшета)
	const [selectedWells, setSelectedWells] = useState<SelectedWell[] | null>(
		null,
	);

	// Сравнивает не ссылку, а содержимое, позволяет избежать несколько лишних рендеров. По хорошему найти места, который обновляют стейт на такой же объект (по содержимому) и сделать чтобы они просто так не обновляли стейт
	const tabletSettings = useSelector(
		(state: RootState) => state.tabletSettings,
		(a, b) => JSON.stringify(a) === JSON.stringify(b),
	);

	const currentRunnerState = useSelector(
		(state: RootState) => state.runner.depth,
	);
	const currentRunnerValue = useSelector(
		(state: RootState) => state.runner.runnerValue,
	);

	// Получение настроек для выбранной скважины или использование значений по умолчанию
	const tabletSettingsByKey =
		(selectedWells && selectedWells[0] && state[selectedWells[0].key]) ||
		defaultTabletSettings;

	const dispatch = useDispatch() as unknown as RootDispatch;

	/**
	 * Копирование настроек планшета для выбранной скважины в локальное состояние
	 * Если скважина уже есть в локальном состоянии, то не перезаписываем её изменения
	 */
	useEffect(() => {
		if (selectedWells) {
			for (const selectedWell of selectedWells) {
				const isInit = selectedWell && selectedWell.key in state;
				if (!isInit && selectedWell) {
					const settings = cloneDeep(
						tabletSettings[selectedWell.key] ?? defaultTabletSettings,
					);
					setState((prev) => ({ ...prev, [selectedWell.key]: settings }));
				}
			}
		}
	}, [selectedWells, state, tabletSettings]);

	/**
	 * Обновление настроек планшета в локальном состоянии
	 * @param value Новые настройки планшета
	 * @param wellKeys Ключи скважин (опционально, по умолчанию используются ключи выбранных скважин)
	 */
	const updateTabletSettings = useCallback(
		(value: Partial<TabletSettings>, wellKeys?: string[]) => {
			setState((prev) => {
				const keys = (wellKeys || selectedWells?.map(({ key }) => key)) ?? [];
				const newState: Record<string, TabletSettings> = { ...prev };
				for (const key of keys) {
					if (!key) return prev;
					newState[key] = { ...prev[key], ...value };
				}

				if (JSON.stringify(newState) === JSON.stringify(prev)) {
					return prev;
				}

				return newState;
			});
		},
		[selectedWells],
	);

	/**
	 * Сохранение изменений настроек планшета в Redux и закрытие модального окна
	 */
	const onSave = useCallback(() => {
		const researchMap = Object.values(researches).reduce(
			(prev, researchTree) => ({
				...prev,
				...mapResearchesByKey(researchTree),
			}),
			{},
		);
		const data = Object.entries(state).map(([wellKey, data]) => {
			return {
				wellKey,
				data: prepareDataByCurrentTemplate(
					data,
					templateCurvesSettings,
					researchMap,
				),
			};
		});

		// Сохраняем текущую позицию runner'а перед обновлением настроек
		const savedRunnerDepth = { ...currentRunnerState };
		const savedRunnerValue = currentRunnerValue;

		const activeReaserches: Record<string, { DepthStart: number }> =
			data[0].data.wellLoggingKeys
				.filter((key): key is keyof typeof researchMap => key in researchMap)
				.map((key) => researchMap[key])
				.reduce(
					(acc, item, index) => {
						acc[Object.keys(researchMap)[index]] = item;
						return acc;
					},
					{} as Record<string, { DepthStart: number }>,
				);

		// Проверяем, нужно ли обновлять позицию runner'а
		// Обновляем только если текущая позиция не установлена или находится вне диапазона активных исследований
		const minDepthStart = Math.min(
			...Object.values(activeReaserches).map((item) => item.DepthStart),
		);
		const shouldUpdateRunnerPosition =
			!savedRunnerDepth.relative || savedRunnerDepth.relative < minDepthStart;

		if (shouldUpdateRunnerPosition) {
			dispatch.runner.setRunnerValue({
				relative: minDepthStart,
			});
		} else {
			// Восстанавливаем сохраненную позицию если она была валидной
			if (savedRunnerValue) {
				dispatch.runner.setRunnerValue(savedRunnerValue);
			}
		}

		dispatch.tabletSettings.setBatchTabletSettings(data);
		onClose();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		state,
		onClose,
		templateCurvesSettings,
		researches,
		dispatch.runner,
		dispatch.tabletSettings,
	]);

	// Значения, предоставляемые через контекст
	const value = {
		tabletSettings: tabletSettingsByKey,
		tabletsSettings: state,
		selectedWells,
		setSelectedWells,
		updateTabletSettings,
		onSave,
		showResearchSettings,
	};

	return (
		<TabletSettingsContext.Provider value={value}>
			{children}
		</TabletSettingsContext.Provider>
	);
};

/**
 * Хук для доступа к контексту настроек планшета
 * @returns Контекст настроек планшета
 */
export const useTabletSettings = () => useContext(TabletSettingsContext);
